import {
  type DetailedHTMLProps,
  type FC,
  type InputHTMLAttributes,
  type ReactNode,
  type RefObject,
  useImperativeHandle,
  useRef,
} from 'react'
import { styled } from '@mui/material'
import { Button, type ButtonProps } from '@dhl-ui/components'

export type PhotoUploadRef = {
  openPhotoInput: () => void
}

type Props = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'accept' | 'hidden' | 'type' | 'ref'
> & {
  'data-testid'?: string
  buttonIcon?: ReactNode
  buttonText?: string
  buttonColor?: ButtonProps['color']
  buttonSize?: ButtonProps['size']
  ref?: RefObject<PhotoUploadRef | null>
}

// Visually-hidden file input. We deliberately do NOT use the `hidden` attribute
// (or `display: none`) because iOS WebKit intermittently fails to dispatch the
// `change` event from display-hidden file inputs after a programmatic `.click()`.
// Keeping the input in the layout (just clipped/transparent) makes the change
// event fire reliably while still being invisible to the user.
const VisuallyHiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
})

export const PhotoUpload: FC<Props> = ({
  buttonIcon,
  buttonText,
  buttonColor = 'primary',
  buttonSize = 'medium',
  'data-testid': dataTestId,
  disabled,
  ref,
  ...inputProps
}) => {
  const fileRef = useRef<HTMLInputElement | null>(null)

  useImperativeHandle(ref, () => ({
    openPhotoInput: () => {
      fileRef.current?.click()
    },
  }))

  return (
    <>
      <VisuallyHiddenInput
        {...inputProps}
        accept="image/jpg,image/jpeg,image/png"
        data-testid={dataTestId ? `${dataTestId}-input` : undefined}
        ref={fileRef}
        type="file"
      />

      {buttonText && (
        <Button
          fullWidth
          data-testid={dataTestId ? `${dataTestId}-button` : undefined}
          disabled={disabled}
          startIcon={buttonIcon}
          size={buttonSize}
          variant="outlined"
          color={buttonColor}
          onClick={() => fileRef.current?.click()}
        >
          {buttonText}
        </Button>
      )}
    </>
  )
}
